import type { ConvoMember, MemberRole, Timestamp } from './types';

/** 그룹 최대 인원 (인터뷰 확정) */
export const MAX_GROUP_MEMBERS = 256;

export interface RoleContext {
  uid: string;
  convoOwnerId: string;
  convoAdminIds: string[];
  member: ConvoMember | undefined;
}

/**
 * 방장 권한 판정.
 * owner는 convo.createdBy로 판정하며, adminIds에 owner도 포함될 수 있다.
 */
export function resolveRole(ctx: RoleContext): MemberRole | null {
  if (!ctx.member) return null;
  if (ctx.member.leftAt) return null;
  if (ctx.uid === ctx.convoOwnerId) return 'owner';
  if (ctx.convoAdminIds.includes(ctx.uid)) return 'admin';
  return 'member';
}

export type Capability =
  | 'send_message'
  | 'edit_own_message'
  | 'delete_own_message'
  | 'delete_any_message'
  | 'pin_any_message'
  /** 리액션 "모두 취소" — 남이 남긴 리액션까지 지우는 권한 (PLAN 5-4장) */
  | 'clear_any_reaction'
  | 'invite_member'
  | 'remove_member'
  | 'promote_admin'
  | 'demote_admin'
  | 'edit_convo'
  | 'delete_convo'
  | 'transfer_ownership';

const MEMBER_CAPS: Capability[] = ['send_message', 'edit_own_message', 'delete_own_message'];
const ADMIN_CAPS: Capability[] = [
  ...MEMBER_CAPS,
  'delete_any_message',
  'pin_any_message',
  'clear_any_reaction',
  'invite_member',
  'remove_member',
  'edit_convo',
];
const OWNER_CAPS: Capability[] = [
  ...ADMIN_CAPS,
  'promote_admin',
  'demote_admin',
  'delete_convo',
  'transfer_ownership',
];

/** 방장 기능 매트릭스 (요구사항 11) */
export function capabilitiesFor(role: MemberRole | null): Capability[] {
  switch (role) {
    case 'owner':
      return OWNER_CAPS;
    case 'admin':
      return ADMIN_CAPS;
    case 'member':
      return MEMBER_CAPS;
    default:
      return [];
  }
}

export function can(role: MemberRole | null, cap: Capability): boolean {
  return capabilitiesFor(role).includes(cap);
}

/** 그룹 인원 상 exceeded 여부 */
export function canAddMember(currentCount: number, capacity = MAX_GROUP_MEMBERS): boolean {
  return currentCount < capacity;
}

/**
 * 방장 퇴실 시 승격자 결정 (PLAN 9장 미결정 21번).
 * 부방장 → 없으면 최초 가입 멤버 → 없으면 null(방 삭제 필요).
 */
export function pickNextOwner(
  ownerId: string,
  adminIds: string[],
  memberIds: string[],
  joinedAtByUid: Record<string, Timestamp>,
): string | null {
  const byJoinedAt = (a: string, b: string) => {
    const at = joinedAtByUid[a]?.seconds ?? Number.MAX_SAFE_INTEGER;
    const bt = joinedAtByUid[b]?.seconds ?? Number.MAX_SAFE_INTEGER;
    return at - bt || a.localeCompare(b);
  };

  const remainingAdmins = adminIds.filter((id) => id !== ownerId && memberIds.includes(id));
  const firstAdmin = [...remainingAdmins].sort(byJoinedAt)[0];
  if (firstAdmin) return firstAdmin;

  const remainingMembers = memberIds.filter((id) => id !== ownerId);
  const firstMember = [...remainingMembers].sort(byJoinedAt)[0];
  return firstMember ?? null;
}
